import type { SchemaObjectsArrayInputMetadata } from '@newfront-insurance/coverage-api';
import type { SchemaInputControlCustomInputProps, SchemaFieldType } from '@newfront-insurance/dsl-schema-ui';
import { SchemaInputControl } from '@newfront-insurance/dsl-schema-ui';
import type { JSX } from 'react';

import type { SchemaObjectsArrayValue } from './schema-objects-array-display';

function orderedPropertyKeys(itemSchema: SchemaFieldType): string[] {
  const fromLayout = itemSchema.layout?.flatMap((group) => group.inputs);
  if (fromLayout?.length) {
    return fromLayout;
  }
  return Object.keys(itemSchema.properties ?? {});
}

export function SchemaObjectsArrayInput({
  value,
  schema,
  name,
  fieldPath,
}: SchemaInputControlCustomInputProps<SchemaObjectsArrayValue, SchemaObjectsArrayInputMetadata>): JSX.Element | null {
  const items = value || [];
  const itemSchema = (schema.properties || schema.items) as SchemaFieldType;

  const propertyKeys = orderedPropertyKeys(itemSchema);

  // Get visible property keys (those with inputType)
  const visibleProperties = propertyKeys
    .map((key) => ({
      key,
      schema: itemSchema.properties?.[key],
    }))
    .filter((prop): prop is { key: string; schema: SchemaFieldType } => Boolean(prop.schema?.inputType));

  return (
    <div className="flex flex-col gap-8">
      {/* Header Row */}
      <div className="flex flex-row items-center gap-8">
        <div className="text-12px w-10 font-bold">#</div>
        {visibleProperties.map(({ key, schema: propSchema }) => (
          <div key={key} className="text-12px flex min-w-60 flex-1 font-bold">
            {propSchema.inputMetadata?.label || key}
          </div>
        ))}
      </div>

      {/* Data Rows */}
      {items.map((_, index) => (
        <div key={index} className="flex flex-row items-center gap-8">
          <div className="text-12px text-steel-500 w-10">{index + 1}</div>
          {visibleProperties.map(({ key, schema: propSchema }) => (
            <div key={key} className="flex min-w-60 flex-1">
              <SchemaInputControl
                name={name}
                type={propSchema.inputType!}
                schema={propSchema}
                metadata={propSchema.inputMetadata}
                fieldPath={`${fieldPath}.${index}.${key}`}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
